import { Promise as BluebirdPromise } from "bluebird";
import moment from "moment";

function getTodayDate(format = "YYYY-MM-DD") {
  return moment().format(format);
}

function copyToClipboard(textToCopy) {
  // navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    // navigator clipboard api method'
    return navigator.clipboard.writeText(textToCopy);
  } else {
    const range = document.createRange();
    const selection = document.getSelection();
    const mark = document.createElement("span");
    mark.textContent = textToCopy;
    // reset user styles for span element
    mark.style.all = "unset";
    // prevents scrolling to the end of the page
    mark.style.position = "fixed";
    mark.style.top = 0;
    mark.style.clip = "rect(0, 0, 0, 0)";
    // used to preserve spaces and line breaks
    mark.style.whiteSpace = "pre";
    // do not inherit user-select (it may be `none`)
    mark.style.webkitUserSelect = "text";
    mark.style.MozUserSelect = "text";
    mark.style.msUserSelect = "text";
    mark.style.userSelect = "text";
    mark.addEventListener("copy", (e) => {
      e.stopPropagation();
    });

    document.body.appendChild(mark);

    // The following line is very important
    if (selection.rangeCount > 0) {
      selection.removeAllRanges();
    }

    range.selectNodeContents(mark);
    selection.addRange(range);

    return new Promise((res, rej) => {
      // here the magic happens
      const copied = document.execCommand("copy");

      if (copied) {
        res(`"${textToCopy}" was copied to clipboard.`);
      } else {
        rej("Error copying text to clipboard.");
      }

      document.body.removeChild(mark);
    });
  }
}

const getBase64 = (file) => {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      res(reader.result);
    };
    reader.onerror = (error) => {
      rej(error);
    };
  });
};

const getFileSize = (file) => {
  if (!file || typeof file.size !== "number") return "";

  const size = file.size;

  if (size < 1024) {
    return `${size.toFixed(0)}KB`;
  } else if (size >= 1024 && size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)}MB`;
  } else {
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)}GB`;
  }
};

const waitFor = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

const promiseAsyncPoll = async (asyncFuncs, concurrency, delay) => {
  return await BluebirdPromise?.map(
    asyncFuncs,
    async (asyncFunc) => {
      if (delay) {
        await waitFor(delay);
      }
      return await asyncFunc();
    },
    { concurrency }
  );
};

const restParamsComposer = (params, startUrl = "", idPessoaLoja) => {
  let paramUrl = startUrl;

  for (const param in params) {
    const paramValue = params[param];

    if (paramValue) {
      if (!paramUrl.length) {
        paramUrl += "?";
      } else {
        paramUrl += "&";
      }

      if (param === "idLoja" && idPessoaLoja) {
        paramUrl += `idPessoaLoja=${paramValue.toString()}`;
      } else {
        paramUrl += `${param}=${paramValue.toString()}`;
      }
    }
  }

  return paramUrl;
};

const arrayEquals = (arr1, arr2) => {
  return (
    Array.isArray(arr1) &&
    Array.isArray(arr2) &&
    arr1.length === arr2.length &&
    arr1.every((val, index) => val === arr2[index])
  );
};

const redirectLoginPage = () => {
  const currentPath = window.location.href;

  if (currentPath.indexOf("/auth/login") < 0) {
    if (window.location.href.includes("karsten-portal-b2b-test")) {
      window.location.href = process.env.PUBLIC_URL + "/index.html?auth/login";
    } else {
      window.location.href = "/auth/login";
    }
  }
};

const getIp = (callback) => {
  function response(s) {
    callback(window.userip);

    s.onload = s.onerror = null;
    document.body.removeChild(s);
  }

  function trigger() {
    window.userip = false;

    const s = document.createElement("script");
    s.async = true;
    s.onload = function () {
      response(s);
    };
    s.onerror = function () {
      response(s);
    };

    s.src = "https://l2.io/ip.js?var=userip";
    document.body.appendChild(s);
  }

  if (/^(interactive|complete)$/i.test(document.readyState)) {
    trigger();
  } else {
    document.addEventListener("DOMContentLoaded", trigger);
  }
};

const normalizeString = (string) => {
  return string
    .toLowerCase()
    .normalize("NFD")
    ?.replace(/[\u0300-\u036f]/g, "");
};

const UtilsFncs = {
  CopyToClipboard(value) {
    return copyToClipboard(value);
  },
  FileToB64(file) {
    return getBase64(file);
  },
  GetFileSize(file) {
    return getFileSize(file);
  },
  async PromiseAsyncPoll(asyncFuncs, limit) {
    return await promiseAsyncPoll(asyncFuncs, limit);
  },
  WaitFor(delay) {
    return waitFor(delay);
  },
  RestParamsComposer(params, startUrl, idPessoaLoja) {
    return restParamsComposer(params, startUrl, idPessoaLoja);
  },
  ArrayEquals(arr1, arr2) {
    return arrayEquals(arr1, arr2);
  },
  TodayDate(format) {
    return getTodayDate(format);
  },
  GetMoment(date) {
    return date ? moment(date) : moment();
  },
  RedirectToLoginPage() {
    return redirectLoginPage();
  },
  GetTimeAgo(time) {
    return moment(time).fromNow();
  },
  CheckSessionUserProfile(nomePerfil) {
    const sessionUserProfiles =
      JSON.parse(localStorage.getItem("userProfiles")) || [];

    const sessionUserProfilesVision =
      JSON.parse(localStorage.getItem("userProfilesVision")) || [];

    const profile = sessionUserProfilesVision
      ? sessionUserProfilesVision
      : sessionUserProfiles;

    return profile.some(
      (profileToCheck) => profileToCheck.NomePerfil === nomePerfil
    );
  },
  IsMobile() {
    return JSON.parse(localStorage.getItem("isMobile"));
  },
  Truncate(string, limit) {
    return string.length > limit ? `${string.substr(0, limit)}...` : string;
  },
  GetIp(callback) {
    return getIp(callback);
  },
  GetUrlSearchParams(search) {
    const params = {};

    if (search?.length) {
      search
        ?.replace("?", "")
        ?.split("&")
        ?.forEach((param) => {
          const [key, value] = param.split("=");

          params[key] = value;
        });
    }

    return params;
  },
  ConvertDateFormat(date, from, to) {
    return date?.split(from).reverse().join(to) || "";
  },
  NormalizeString(string) {
    return normalizeString(string);
  },
};

export default UtilsFncs;
